'use client'

import Link from 'next/link'
import React from 'react'

export interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: 'primary' | 'secondary' | 'outline' | 'ghost' | 'light' | 'dark'
  size?: 'sm' | 'md' | 'lg'
  isLoading?: boolean
  leftIcon?: React.ReactNode
  rightIcon?: React.ReactNode
  className?: string
  href?: string
  children: React.ReactNode
}

const Button: React.FC<ButtonProps> = ({
  variant = 'primary',
  size,
  isLoading,
  leftIcon,
  rightIcon,
  className = '',
  href,
  children,
  ...props
}) => {
  const baseStyles = 'inline-flex items-center justify-center rounded-lg font-semibold transition-all duration-300'
  const variantStyles = {
    primary: 'bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white shadow-md hover:shadow-lg px-6 py-3 text-base',
    secondary: 'bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white shadow-md hover:shadow-lg px-6 py-3 text-base',
    outline: 'bg-white text-gray-900 hover:bg-gray-100 px-6 py-3 text-base border border-gray-300',
    ghost: 'bg-transparent text-gray-900 hover:bg-gray-100 px-6 py-3 text-base',
    light: 'bg-white text-gray-900 hover:bg-gray-100 px-6 py-3 text-base',
    dark: 'bg-gray-900 text-white hover:bg-gray-800 px-6 py-3 text-base'
  }

  const sizeStyles = size ? {
    sm: 'text-sm',
    md: 'text-base',
    lg: 'text-lg',
  }[size] : ''

  const buttonStyles = `${baseStyles} ${variantStyles[variant]} ${sizeStyles} ${className}`

  const content = (
    <>
      {isLoading && (
        <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-current" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
        </svg>
      )}
      {leftIcon && <span className="mr-2">{leftIcon}</span>}
      {children}
      {rightIcon && <span className="ml-2">{rightIcon}</span>}
    </>
  )

  if (href) {
    return (
      <Link href={href} className={buttonStyles}>
        {content}
      </Link>
    )
  }

  return (
    <button className={buttonStyles} disabled={isLoading} {...props}>
      {content}
    </button>
  )
}

export default Button 